import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import * as API from '@/services/api.js'
import { AnalyticsService } from '@/services/analytics.js'
import { handleError } from '@/services/errorHandler.js'
import { usePaymentContext } from '@/context-state/PaymentContext.jsx'
import { toggleAppBanner } from '@/hooks/useAppBanner.jsx'
import { EntitlementsKeys } from '@/guards/EntitlementsGuard.jsx'

export const ViewsListContext = createContext(null)

// Dashboards render in their saved drag-order. `order` may be missing on older
// views — those fall to the end, then keep their relative arrival order.
const sortByOrder = (list) => {
    return [...list].sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
}

export function ViewsListProvider({ children }) {

    const [views, setViews] = useState([])
    const [isFetched, setIsFetched] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    // When the space changes, drop the previous space's list (and its fetched flag) so we
    // never show or navigate to dashboards that belong to another space — the lazy fetchers
    // (DashboardsNavSection / ViewLayout) then refetch for the new space.
    const { spaceId } = useParams()
    const { checkEntitlementAvailable } = usePaymentContext()
    const currentSpaceRef = useRef(spaceId)
    useEffect(() => {
        if (currentSpaceRef.current === spaceId) {
            return
        }
        currentSpaceRef.current = spaceId
        setViews([])
        setIsFetched(false)
    }, [spaceId])

    const fetchViews = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await API.listViews()
            setViews(sortByOrder(data))
            setIsFetched(true)
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        } finally {
            setIsLoading(false)
        }
    }, [])

    const createView = useCallback(async (payload, { system = false } = {}) => {
        // Free tier keeps its default dashboards but can't create new ones.
        // Backstop for every create entry point; the buttons are gated too.
        // `system` creates (the auto-seeded default dashboards) bypass the gate —
        // those ARE the free defaults every account gets on connect.
        if (!system && !checkEntitlementAvailable(EntitlementsKeys.VIEWS_MANAGE).available) {
            toggleAppBanner('free-views-limit', 'views_create')
            return null
        }

        try {
            const { data } = await API.createView(payload)
            setViews(prev => [...prev, data])
            return data
        } catch (e) {
            handleError(e)
            toast.error(e.message)
            throw e
        }
    }, [checkEntitlementAvailable])

    const deleteView = useCallback(async (viewId) => {
        try {
            await API.deleteView(viewId)
            setViews(prev => prev.filter(v => v._id !== viewId))
            toast.success('Dashboard deleted')
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        }
    }, [])

    const duplicateView = useCallback(async (viewId) => {
        // Duplicating creates a new dashboard, so it's gated the same as create.
        if (!checkEntitlementAvailable(EntitlementsKeys.VIEWS_MANAGE).available) {
            toggleAppBanner('free-views-limit', 'views_duplicate')
            return null
        }

        try {
            const { data } = await API.duplicateView(viewId)
            setViews(prev => [...prev, data])
            return data
        } catch (e) {
            handleError(e)
            toast.error(e.message)
            throw e
        }
    }, [checkEntitlementAvailable])

    const updateViewInList = useCallback((updated) => {
        setViews(prev => prev.map(v => v._id === updated._id ? updated : v))
    }, [])

    const renameView = useCallback(async (viewId, title) => {
        setViews(prev => prev.map(v => v._id === viewId ? { ...v, title } : v))
        try {
            await API.updateView(viewId, { title })
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        }
    }, [])

    const changeViewIcon = useCallback(async (viewId, icon) => {
        setViews(prev => prev.map(v => v._id === viewId ? { ...v, icon } : v))
        try {
            await API.updateView(viewId, { icon })
            AnalyticsService.track('view-icon-changed', { viewId, icon: icon.value, color: icon.color })
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        }
    }, [])

    // Persist a new drag-order. Caller passes the fully reordered list; we stamp
    // each view's `order` to its index and save them in parallel (optimistic).
    const reorderViews = useCallback(async (orderedViews) => {
        const withOrder = orderedViews.map((v, i) => ({ ...v, order: i }))
        setViews(withOrder)
        try {
            await Promise.all(withOrder.map(v => API.updateView(v._id, { order: v.order })))
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        }
    }, [])

    return (
        <ViewsListContext.Provider value={{
            views,
            isFetched,
            isLoading,
            fetchViews,
            createView,
            deleteView,
            duplicateView,
            updateViewInList,
            renameView,
            changeViewIcon,
            reorderViews,
        }}>
            {children}
        </ViewsListContext.Provider>
    )
}

export const useViewsListContext = () => {
    const context = useContext(ViewsListContext)
    if (!context) {
        throw new Error('useViewsListContext must be used inside ViewsListProvider')
    }
    return context
}
